import {
  TICKET_CHANNELS,
  type Product,
  type ProductCategory,
  type RecordReferenceType,
  type TicketChannel,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  useAddCategory,
  useAddRecordReferenceType,
  useProductConfiguration,
  useRemoveCategory,
  useRemoveRecordReferenceType,
  useSetRecordReferenceRule,
  useUpdateCategory,
  useUpdateProductConfiguration,
  useUpdateRecordReferenceType,
} from '../../../api/products';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { CategoriesSection } from './CategoriesSection';
import { CategoryDialog } from './CategoryDialog';
import { CommittentiSection } from './CommittentiSection';
import { MinDescriptionSection } from './MinDescriptionSection';
import { RecordReferenceTypeDialog } from './RecordReferenceTypeDialog';
import { RecordReferenceTypesSection } from './RecordReferenceTypesSection';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'rename'; category: ProductCategory }
  | { kind: 'remove'; category: ProductCategory }
  | { kind: 'edit-type'; type: RecordReferenceType }
  | { kind: 'remove-type'; type: RecordReferenceType };

export interface ProductConfigurationPanelProps {
  product: Product;
  /** Esito da mostrare nella Snackbar della pagina. */
  onNotice: (message: string) => void;
}

/**
 * Configurazione del prodotto selezionato: lunghezza minima, i due set di categorie, tipi di
 * riferimento al record e regole per committente.
 */
export function ProductConfigurationPanel({ product, onNotice }: ProductConfigurationPanelProps) {
  const configuration = useProductConfiguration(product.id);
  const updateConfiguration = useUpdateProductConfiguration();
  const addCategory = useAddCategory();
  const updateCategory = useUpdateCategory();
  // Mutazione propria per le frecce: il suo errore va sotto il set, non nel dialogo di rinomina.
  const moveCategory = useUpdateCategory();
  const [moveFailure, setMoveFailure] = useState<{ channel: TicketChannel; message: string } | null>(null);
  const removeCategory = useRemoveCategory();
  const addType = useAddRecordReferenceType();
  const updateType = useUpdateRecordReferenceType();
  const removeType = useRemoveRecordReferenceType();
  const setRecordReferenceRule = useSetRecordReferenceRule();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const productId = product.id;

  const close = () => {
    setDialog({ kind: 'closed' });
    updateCategory.reset();
    removeCategory.reset();
    updateType.reset();
    removeType.reset();
  };

  const busy =
    updateConfiguration.isPending ||
    addCategory.isPending ||
    updateCategory.isPending ||
    moveCategory.isPending ||
    removeCategory.isPending ||
    addType.isPending ||
    updateType.isPending ||
    removeType.isPending ||
    setRecordReferenceRule.isPending;

  if (configuration.isPending) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" role="status">
        <CircularProgress size={20} />
        <Typography>Caricamento della configurazione di {product.name}…</Typography>
      </Stack>
    );
  }

  if (configuration.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void configuration.refetch()}>
            Riprova
          </Button>
        }
      >
        <AlertTitle>Configurazione non disponibile</AlertTitle>
        {configuration.error.message}
      </Alert>
    );
  }

  const { data } = configuration;

  return (
    <Stack
      spacing={4}
      sx={{ flex: 1, minWidth: 0 }}
      component="section"
      aria-label={`Configurazione di ${product.name}`}
    >
      <MinDescriptionSection
        key={`${productId}-${data.minDescriptionLength}`}
        value={data.minDescriptionLength}
        busy={busy}
        error={updateConfiguration.error?.message}
        onSave={(input) =>
          updateConfiguration.mutate(
            { productId, changes: { minDescriptionLength: Number(input.minDescriptionLength) } },
            { onSuccess: () => onNotice('Lunghezza minima aggiornata: vale per le segnalazioni successive.') }
          )
        }
      />

      {TICKET_CHANNELS.map((channel) => (
        <CategoriesSection
          key={channel}
          channel={channel}
          categories={data.categories[channel]}
          busy={busy}
          addError={addCategory.variables?.input.channel === channel ? addCategory.error?.message : null}
          onAdd={(name) => addCategory.mutateAsync({ productId, input: { channel, name } })}
          error={moveFailure?.channel === channel ? moveFailure.message : null}
          onMove={(category, position) =>
            moveCategory.mutate(
              { productId, categoryId: category.id, changes: { position } },
              {
                onSuccess: () => setMoveFailure(null),
                onError: (error) => {
                  setMoveFailure({ channel, message: `Spostamento non riuscito: ${error.message}` });
                  // Il set potrebbe essere cambiato altrove (es. voce già rimossa): lo rileggiamo.
                  void configuration.refetch();
                },
              }
            )
          }
          onRename={(category) => setDialog({ kind: 'rename', category })}
          onRemove={(category) => setDialog({ kind: 'remove', category })}
        />
      ))}

      <RecordReferenceTypesSection
        types={data.recordReferenceTypes}
        busy={busy}
        addError={addType.error?.message}
        onAdd={(input) =>
          addType.mutateAsync(
            { productId, input },
            { onSuccess: () => onNotice(`Tipo «${input.label}» aggiunto: vale per le segnalazioni successive.`) }
          )
        }
        onEdit={(type) => setDialog({ kind: 'edit-type', type })}
        onRemove={(type) => setDialog({ kind: 'remove-type', type })}
      />

      <CommittentiSection
        committenti={data.committenti}
        busy={busy}
        error={setRecordReferenceRule.error?.message}
        onToggle={(rule, allowRecordReference) =>
          setRecordReferenceRule.mutate(
            { productId, committente: rule.committente, rule: { allowRecordReference } },
            {
              onSuccess: () =>
                onNotice(
                  `${rule.committente}: riferimento al record ${allowRecordReference ? 'consentito' : 'non consentito'} dalle prossime segnalazioni.`
                ),
            }
          )
        }
      />

      {dialog.kind === 'edit-type' && (
        <RecordReferenceTypeDialog
          open
          key={dialog.type.key}
          type={dialog.type}
          error={updateType.error?.message}
          busy={updateType.isPending}
          onSubmit={(label) =>
            updateType.mutate(
              { productId, key: dialog.type.key, label },
              {
                onSuccess: () => {
                  close();
                  onNotice(`Etichetta aggiornata in «${label}».`);
                },
              }
            )
          }
          onClose={close}
        />
      )}

      {dialog.kind === 'remove-type' && (
        <ConfirmDialog
          open
          title={`Rimuovere «${dialog.type.label}»?`}
          confirmLabel="Rimuovi"
          confirmColor="error"
          error={removeType.error?.message}
          blocked={removeType.error?.code === 'products.record_reference_type_not_found'}
          busy={removeType.isPending}
          onConfirm={() =>
            removeType.mutate(
              { productId, key: dialog.type.key },
              {
                onSuccess: () => {
                  close();
                  onNotice(`Tipo «${dialog.type.label}» rimosso.`);
                },
              }
            )
          }
          onClose={close}
        >
          Dalle segnalazioni successive EasyHelp rifiuta un riferimento con la chiave «{dialog.type.key}». I ticket già
          ricevuti non cambiano: conservano il riferimento e l&apos;etichetta con cui sono nati.
        </ConfirmDialog>
      )}

      {dialog.kind === 'rename' && (
        <CategoryDialog
          open
          key={dialog.category.id}
          category={dialog.category}
          error={updateCategory.error?.message}
          busy={updateCategory.isPending}
          onSubmit={(name) =>
            updateCategory.mutate(
              { productId, categoryId: dialog.category.id, changes: { name } },
              {
                onSuccess: () => {
                  close();
                  onNotice(`Categoria rinominata in «${name}».`);
                },
              }
            )
          }
          onClose={close}
        />
      )}

      {dialog.kind === 'remove' && (
        <ConfirmDialog
          open
          title={`Rimuovere «${dialog.category.name}»?`}
          confirmLabel="Rimuovi"
          confirmColor="error"
          error={removeCategory.error?.message}
          blocked={removeCategory.error?.code === 'products.category_not_found'}
          busy={removeCategory.isPending}
          onConfirm={() =>
            removeCategory.mutate(
              { productId, categoryId: dialog.category.id },
              {
                onSuccess: () => {
                  close();
                  onNotice(`Categoria «${dialog.category.name}» rimossa dal set.`);
                },
              }
            )
          }
          onClose={close}
        >
          La voce sparisce dal form del prodotto per le segnalazioni successive. I ticket già aperti con questa
          categoria non cambiano: conservano il nome con cui sono nati.
        </ConfirmDialog>
      )}
    </Stack>
  );
}
